import React, { useState } from 'react';
import { User, Calendar, Target, FileText, Building, Briefcase, Clock, Edit, Send, AlertTriangle, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';

const EmployeeHomeDashboard = () => {
  const [currentUser] = React.useState(null); // No mock data - will be loaded from API

  const handleEditProfile = () => {
    // TODO: Navigate to profile edit form
    console.log('Navigate to edit profile');
  };

  const handleRequestTimeOff = () => {
    // TODO: Navigate to time-off request form
    console.log('Navigate to time-off request');
  };

  const handleViewGoals = () => {
    // TODO: Navigate to goals/performance page
    console.log('Navigate to goals');
  };

  const handleViewDocuments = () => {
    // TODO: Navigate to documents page
    console.log('Navigate to documents');
  };

  const handleViewOrgChart = () => {
    // TODO: Navigate to org chart
    console.log('Navigate to org chart');
  };

  const handleViewJobs = () => {
    // TODO: Navigate to internal jobs/careers page
    console.log('Navigate to internal jobs');
  };

  // Configuration banner for backend setup
  const ConfigBanner = () => (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-5 w-5 text-blue-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-blue-700">
            <strong>Backend not configured.</strong> Set NEXT_PUBLIC_API_URL and wire endpoints to enable data.
          </p>
        </div>
      </div>
    </div>
  );

  // Welcome section with personalized greeting
  const WelcomeSection = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex items-center">
        <div className="h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center">
          <User className="h-6 w-6 text-gray-400" />
        </div>
        <div className="ml-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Welcome back!
          </h2>
          <p className="text-sm text-gray-600">
            Connect your backend to load your personal information and get started.
          </p>
        </div>
      </div>
    </div>
  );

  // Profile summary card
  const ProfileCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">My Profile</h3>
        <span className="text-sm text-gray-500">Personal Information</span>
      </div>
      <EmptyState
        icon={User}
        title="Profile not loaded"
        description="Connect backend to view your profile information, job details, and personal settings."
        primaryAction={{
          label: "Edit Limited Fields",
          onClick: handleEditProfile,
          icon: Edit
        }}
        secondaryAction={undefined}
      />
    </div>
  );

  // Time off balance card
  const TimeOffCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">My Time Off</h3>
        <Clock className="h-5 w-5 text-gray-400" />
      </div>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Available Balance</span>
          <span className="text-lg font-semibold text-gray-900">-- days</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Used This Year</span>
          <span className="text-sm text-gray-500">-- days</span>
        </div>
        <button
          onClick={handleRequestTimeOff}
          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
        >
          <Send className="h-4 w-4 mr-2" />
          Request Time Off
        </button>
      </div>
    </div>
  );

  // Goals and performance card
  const GoalsCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">My Goals &amp; 1:1s</h3>
        <Target className="h-5 w-5 text-gray-400" />
      </div>
      <EmptyState
        icon={Target}
        title="No goals set"
        description="Set personal goals and track your progress. Schedule 1:1 meetings with your manager."
        primaryAction={{
          label: "View Goals",
          onClick: handleViewGoals,
          icon: Target
        }}
      />
    </div>
  );

  // Documents card
  const DocumentsCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">My Documents</h3>
        <FileText className="h-5 w-5 text-gray-400" />
      </div>
      <EmptyState
        icon={FileText}
        title="No documents"
        description="Access company policies, signed documents, and personal files."
        primaryAction={{
          label: "View Documents",
          onClick: handleViewDocuments,
          icon: FileText
        }}
        secondaryAction={undefined}
      />
    </div>
  );

  // Company info card
  const CompanyCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Company</h3>
        <Building className="h-5 w-5 text-gray-400" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center text-sm">
          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-gray-600">Next Holiday:</span>
          <span className="ml-2 text-gray-900">Connect backend to load</span>
        </div>
        <button
          onClick={handleViewOrgChart}
          className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-gray-300 transition-colors"
        >
          <div className="flex items-center">
            <Users className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-900">View Org Chart</span>
          </div>
        </button>
      </div>
    </div>
  );

  // Internal careers card
  const CareersCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Careers/Internal Jobs</h3>
        <Briefcase className="h-5 w-5 text-gray-400" />
      </div>
      <EmptyState
        icon={Briefcase}
        title="No open positions"
        description="Explore internal career opportunities and professional development paths."
        primaryAction={{
          label: "View Job Board",
          onClick: handleViewJobs,
          icon: Briefcase
        }}
        secondaryAction={undefined}
      />
    </div>
  );

  const pageActions = [
    {
      label: "Edit Profile",
      onClick: handleEditProfile,
      icon: Edit,
      variant: "outline"
    },
    {
      label: "Request Time Off",
      onClick: handleRequestTimeOff,
      icon: Calendar,
      variant: "default"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="My Dashboard"
        subtitle="Your personalized workspace for HR information and requests"
        actions={pageActions}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ConfigBanner />
        <WelcomeSection />
        
        {/* Personal Information Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <ProfileCard />
          <TimeOffCard />
          <GoalsCard />
          <DocumentsCard />
          <CompanyCard />
          <CareersCard />
        </div>
      </div>
    </div>
  );
};

export default EmployeeHomeDashboard;